/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from "three";
import React, { useEffect, useMemo, useRef } from 'react';

import { const_var, params } from '../../../../../redux/reducers/reducer';
import { controls } from '../../../../../redux/reducers/common/eventReducer';

import { extrudeSettings, railModel } from '../../../../../utils/Functions';
import PillarRailModel from '../../Legs';
import BaseRailModel from '../../BaseRail';
import CommonTruss from '../CommonTruss';
import { MaterialTruss } from "../../../CustomMaterial";
import { trussDimensions } from "../../../../../helpers/building_component/truss/truss.helper";

const roofRailDistanceY = 1;
const hangRailDivision = 1 / 2;

const CTCT = ({ trussType, normalMap, envMap, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, meshDelModel_CSG, isFor  }) => {

    return (
        <>
            {isFor == 'center' && <BaseRailModel normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} />}
            {isFor == 'center' && <PillarRailModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} roofRailDistanceY={roofRailDistanceY} hangRailDivision={hangRailDivision} />}
            <TrussModel trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} normalMap={normalMap} envMap={envMap} meshDelModel_CSG={meshDelModel_CSG} overlapTrussArr={overlapTrussArr} hiddenBaseRailIdArr={hiddenBaseRailIdArr} isFor={isFor}/>
        </>
    )
}

export default CTCT

const TrussModel = ({ trussType, trussModelPosInfoArr, normalMap, envMap, overlapTrussArr, hiddenBaseRailIdArr, isFor }) => {
    const obj_centerHRail = new THREE.Object3D();
    const obj_centerVRail = new THREE.Object3D();
    const obj_connectLongSlopeRail = new THREE.Object3D();
    const obj_connectShortSlopeRail = new THREE.Object3D();
    const obj_underTruss = new THREE.Object3D();
    const obj_endLongSlopeRail = new THREE.Object3D();
    const obj_endShortSlopeRail = new THREE.Object3D();
    const vector = new THREE.Vector3(1, 0, 0)

    const ref_centerHRail = useRef();
    const ref_centerVRail = useRef();
    const ref_connectLongSlopeRail = useRef();
    const ref_connectShortSlopeRail = useRef();
    const ref_underTruss = useRef();
    const ref_endLongSlopeRail= useRef();
    const ref_endShortSlopeRail= useRef();

    const bottomBaseRailWidth = 0.3;
    const bottomBaseRailHeight = 0.1;
    const connectRailWidth = 0.3;
    const connectRailHeight = 0.2;
    const offset = 0.035;

    const trussDimension = trussDimensions(isFor)

    const alignPosOffset = useMemo(() => {
        if(params.p_r_s === 1 ) return const_var.roofRegularDst * 2;
        else if(params.p_r_s === 2 || params.p_r_s === 3) return 0;
        else return 0;
    }, [params.p_r_s, trussDimension.roofPitch])

    const roofAlpha = Math.atan(trussDimension.roofPitch / 12);
    const roofHeight = (trussDimension.buildingWidth / 2) * (trussDimension.roofPitch / 12);
    const roofOuterRailLength = useMemo(() => (Math.sqrt(Math.pow(trussDimension.buildingWidth / 2, 2) + Math.pow(roofHeight, 2))), [trussDimension.buildingWidth, roofHeight]);
    const bottomBaseRailLength = (trussDimension.buildingWidth - bottomBaseRailHeight / 2 - connectRailWidth / 2) * hangRailDivision;
    const roofCenterRailHeight = ((trussDimension.buildingWidth / 2) * trussDimension.roofPitch / 12) * hangRailDivision + roofRailDistanceY;
    const distance = 2;
    const count = Math.floor((trussDimension.buildingWidth / 2) / distance);
    
    const connectLongRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];
        let verticalRailLength = 0;

        for (let i = 1; i <= count ; i += 2) {
            let pos_x = distance * i - connectRailWidth / 2;
            let pos_y = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - pos_x) * (trussDimension.roofPitch / 12) - roofRailDistanceY + 0.03;
            verticalRailLength = Math.sqrt(Math.pow(distance, 2) + Math.pow((roofRailDistanceY + distance * Math.tan(roofAlpha)), 2));
            let alpha = Math.asin(distance / verticalRailLength);
            
            let tempInfoObj;

            tempInfoObj = {
                distance: distance,
                pos_x: pos_x,
                pos_y: pos_y,
                length: verticalRailLength,
                alpha: alpha
            }


            dataInfoArray.push(tempInfoObj);
        }

        return {
            verticalRailLength,
            dataInfoArray
        }
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofAlpha, trussDimension.roofPitch])

    const longSlopeModelDataArr = useMemo(() => {
        let pos_x, pos_y, length, alpha
        let tempInfoObj;
        if(count % 2 === 0) {
            const remainSize = trussDimension.buildingWidth / 2 - distance * (count - 1);
            
            pos_x = trussDimension.buildingWidth / 2 - connectRailWidth / 2;
            pos_y = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - pos_x) * (trussDimension.roofPitch / 12) - roofRailDistanceY;
            length = Math.sqrt(Math.pow((remainSize - distance), 2) + Math.pow((roofRailDistanceY + (remainSize - distance) * Math.tan(roofAlpha)), 2));
            alpha = Math.asin((remainSize - distance) / length);
        }  
        
        tempInfoObj = {
            distance: distance,
            pos_x: pos_x,
            pos_y: pos_y,
            length: length,
            alpha: alpha
        }
        
        return tempInfoObj
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofAlpha, trussDimension.roofPitch])
        
    const connectShortRailModelDataArr = useMemo(() => {
        let dataInfoArray = [];
        let verticalRailLength = 0;

        for (let i = 1; i <= count - 1; i += 2) {
            const pos_x = distance * i - connectRailWidth / 2;
            const pos_y = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - pos_x) * (trussDimension.roofPitch / 12) - roofRailDistanceY;
            verticalRailLength = Math.sqrt(Math.pow(distance, 2) + Math.pow((roofRailDistanceY - distance * Math.tan(roofAlpha)), 2));
            let alpha = Math.PI / 2 - Math.acos(distance / verticalRailLength);

            let tempInfoObj;
            
            tempInfoObj = {
                distance: distance,
                pos_x: pos_x,
                pos_y: pos_y,
                length: verticalRailLength,
                alpha: alpha
            }

            dataInfoArray.push(tempInfoObj);
        }

        return {
            verticalRailLength,
            dataInfoArray
        }
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofAlpha, trussDimension.roofPitch])

    const shortSlopeModelDataArr = useMemo(() => {
        let length, alpha
        let tempInfoObj;

        const remainSize = trussDimension.buildingWidth / 2 - distance * count;
        const pos_x = distance * count - connectRailWidth / 2;
        const pos_y = trussDimension.buildingHeight + (trussDimension.buildingWidth / 2 - pos_x) * (trussDimension.roofPitch / 12) - roofRailDistanceY;
        if (remainSize < distance) {
            if (remainSize < 0.5) {
                length = 0;
                alpha = Math.PI / 2;
            } else {
                length = Math.sqrt(Math.pow(remainSize, 2) + Math.pow((roofRailDistanceY - remainSize * Math.tan(roofAlpha)), 2));
                alpha = Math.PI / 2 - Math.acos(remainSize / length);
            }
        }
            
        tempInfoObj = {
            distance: distance,
            pos_x: pos_x,
            pos_y: pos_y,
            length: length,
            alpha: alpha
        }

        return tempInfoObj
    }, [trussDimension.buildingHeight, trussDimension.buildingWidth, roofAlpha, trussDimension.roofPitch])


    useEffect(() => {
        if (ref_centerHRail.current && ref_centerVRail.current && ref_underTruss.current && ref_connectLongSlopeRail.current && ref_connectShortSlopeRail.current && ref_endLongSlopeRail.current && ref_endShortSlopeRail.current) {
            let index_all_1 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 1) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    //----- roof under rail-----
                    obj_underTruss.position.set(offsetForDir * (-trussDimension.buildingWidth / 2 + connectRailWidth / 4), trussDimension.buildingHeight - connectRailHeight / 2 - roofRailDistanceY + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_underTruss.rotation.y = offsetForDir * Math.PI / 2;
                    obj_underTruss.rotateOnAxis(vector, offsetForDir * roofAlpha);
                    //--------------------------

                    //----- bottom base rail-----
                    obj_centerHRail.position.set(-bottomBaseRailLength / 2, trussDimension.buildingHeight + roofHeight / 2 - roofRailDistanceY - bottomBaseRailHeight / 2 + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerHRail.rotation.y = Math.PI / 2;
                    //---------------------

                    //----- roof center rail-----
                    obj_centerVRail.position.set(0, trussDimension.buildingHeight + roofHeight / 2 - roofRailDistanceY - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_centerVRail.rotation.x = -Math.PI / 2;
                    //---------------------

                    //----- end long slope rail-----
                    obj_endLongSlopeRail.position.set(offsetForDir * longSlopeModelDataArr.pos_x, longSlopeModelDataArr.pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_endLongSlopeRail.rotation.y = offsetForDir * -Math.PI / 2;

                    obj_endLongSlopeRail.rotateOnAxis(vector, offsetForDir * (Math.PI / 2 - longSlopeModelDataArr.alpha));
                    if (offsetForDir === -1) {
                        obj_endLongSlopeRail.rotateOnAxis(vector, offsetForDir * (-Math.PI / 2 + longSlopeModelDataArr.alpha));

                        obj_endLongSlopeRail.rotation.x = 0;
                        obj_endLongSlopeRail.rotateOnAxis(vector,  (-Math.PI / 2 + longSlopeModelDataArr.alpha));
                    }

                    obj_endShortSlopeRail.position.set(offsetForDir * shortSlopeModelDataArr.pos_x, shortSlopeModelDataArr.pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                    obj_endShortSlopeRail.rotation.y = offsetForDir * Math.PI / 2;

                    obj_endShortSlopeRail.rotateOnAxis(vector, offsetForDir * (Math.PI / 2 - shortSlopeModelDataArr.alpha));
                    //---------------------

                    if (hiddenBaseRailIdArr.includes(index_truss)) {
                        obj_underTruss.scale.setScalar(0);
                        obj_centerHRail.scale.setScalar(0);
                        obj_centerVRail.scale.setScalar(0);
                        obj_endLongSlopeRail.scale.setScalar(0);
                        obj_endShortSlopeRail.scale.setScalar(0);
                    } else {
                        obj_underTruss.scale.setScalar(1);
                        obj_centerHRail.scale.setScalar(1);
                        obj_centerVRail.scale.setScalar(1);
                        obj_endLongSlopeRail.scale.setScalar(1);
                        obj_endShortSlopeRail.scale.setScalar(1);
                    }

                    if (params.p_r_s === 1) obj_endShortSlopeRail.scale.setScalar(0);

                    obj_underTruss.updateMatrix();
                    ref_underTruss.current.setMatrixAt(index_all_1, obj_underTruss.matrix);
                    obj_centerVRail.updateMatrix();
                    ref_centerVRail.current.setMatrixAt(index_all_1, obj_centerVRail.matrix);
                    obj_centerHRail.updateMatrix();
                    ref_centerHRail.current.setMatrixAt(index_all_1, obj_centerHRail.matrix);
                    obj_endLongSlopeRail.updateMatrix();
                    ref_endLongSlopeRail.current.setMatrixAt(index_all_1, obj_endLongSlopeRail.matrix);
                    obj_endShortSlopeRail.updateMatrix();
                    ref_endShortSlopeRail.current.setMatrixAt(index_all_1, obj_endShortSlopeRail.matrix);

                    index_all_1 ++;
                })
            })

            let index_all_2 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectLongRailModelDataArr.dataInfoArray.length).fill('').forEach((_, index_rail) => {
                        obj_connectLongSlopeRail.position.set(offsetForDir * connectLongRailModelDataArr.dataInfoArray[index_rail].pos_x, connectLongRailModelDataArr.dataInfoArray[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectLongSlopeRail.rotation.y = offsetForDir * Math.PI / 2;
                        obj_connectLongSlopeRail.rotateOnAxis(vector, offsetForDir * (-Math.PI / 2 - connectLongRailModelDataArr.dataInfoArray[index_rail].alpha));

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_connectLongSlopeRail.scale.setScalar(0);
                        } else {
                            obj_connectLongSlopeRail.scale.setScalar(1);
                        }

                        obj_connectLongSlopeRail.updateMatrix();
                        ref_connectLongSlopeRail.current.setMatrixAt(index_all_2, obj_connectLongSlopeRail.matrix);

                        index_all_2 ++;
                    })
                })
            })

            let index_all_3 = 0;
            new Array(2).fill('').forEach((_, index_dir) => {
                let offsetForDir = 1;
                if (index_dir === 0) offsetForDir = 1;
                else offsetForDir = -1;

                new Array(trussModelPosInfoArr.length).fill('').forEach((_, index_truss) => {
                    new Array(connectShortRailModelDataArr.dataInfoArray.length).fill('').forEach((_, index_rail) => {
                        obj_connectShortSlopeRail.position.set(offsetForDir * connectShortRailModelDataArr.dataInfoArray[index_rail].pos_x, connectShortRailModelDataArr.dataInfoArray[index_rail].pos_y - bottomBaseRailHeight + alignPosOffset, trussModelPosInfoArr[index_truss]);
                        obj_connectShortSlopeRail.rotation.y = offsetForDir * Math.PI / 2;
                        obj_connectShortSlopeRail.rotateOnAxis(vector, offsetForDir * ( -Math.PI / 2 + connectShortRailModelDataArr.dataInfoArray[index_rail].alpha));

                        if (hiddenBaseRailIdArr.includes(index_truss)) {
                            obj_connectShortSlopeRail.scale.setScalar(0);
                        } else {
                            obj_connectShortSlopeRail.scale.setScalar(1);
                        }

                        obj_connectShortSlopeRail.updateMatrix();
                        ref_connectShortSlopeRail.current.setMatrixAt(index_all_3, obj_connectShortSlopeRail.matrix);

                        index_all_3 ++;
                    })
                })
            })
            
            ref_centerHRail.current.instanceMatrix.needsUpdate = true;
            ref_centerVRail.current.instanceMatrix.needsUpdate = true;
            ref_connectLongSlopeRail.current.instanceMatrix.needsUpdate = true;
            ref_connectShortSlopeRail.current.instanceMatrix.needsUpdate = true;
            ref_underTruss.current.instanceMatrix.needsUpdate = true;
            ref_endLongSlopeRail.current.instanceMatrix.needsUpdate = true;
            ref_endShortSlopeRail.current.instanceMatrix.needsUpdate = true;
        }
    }, [ref_centerHRail.current, ref_centerVRail.current, ref_underTruss.current, ref_connectLongSlopeRail.current, ref_connectShortSlopeRail.current, ref_endLongSlopeRail.current, ref_endShortSlopeRail.current, trussDimension.buildingWidth, trussDimension.buildingHeight, connectLongRailModelDataArr, connectShortRailModelDataArr, trussModelPosInfoArr, overlapTrussArr, hiddenBaseRailIdArr, alignPosOffset, longSlopeModelDataArr, shortSlopeModelDataArr, roofAlpha, controls.isCheckActiveTab, params.activeWall])

    return (
        <group position={[0, -offset, 0]} rotation={[0,trussDimension.trussRotation,0]} name='CTCT-truss-legs'>
            <CommonTruss trussType={trussType} trussModelPosInfoArr={trussModelPosInfoArr} overlapTrussArr={overlapTrussArr} normalMap={normalMap} envMap={envMap} roofRailDistanceY={roofRailDistanceY} isFor={isFor} />
            <>
                {/* --- roof unnder truss model --- */}
                <instancedMesh ref={ref_underTruss} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(roofOuterRailLength - 0.04)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}
                
                {/* --- bottom base rail model --- */}
                <instancedMesh   instancedMesh ref={ref_centerHRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(bottomBaseRailWidth, bottomBaseRailHeight), extrudeSettings(bottomBaseRailLength)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- roof center rail model --- */}
                <instancedMesh ref={ref_centerVRail} args={[null, null, trussModelPosInfoArr.length]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailHeight, connectRailWidth), extrudeSettings(roofCenterRailHeight)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}

                {/* --- connect rail model --- */}
                <instancedMesh ref={ref_connectLongSlopeRail} args={[null, null, trussModelPosInfoArr.length * connectLongRailModelDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectLongRailModelDataArr.verticalRailLength - 0.1)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                <instancedMesh ref={ref_endLongSlopeRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(longSlopeModelDataArr.length - 0.1)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>

                <instancedMesh ref={ref_connectShortSlopeRail} args={[null, null, trussModelPosInfoArr.length * connectShortRailModelDataArr.dataInfoArray.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(connectShortRailModelDataArr.verticalRailLength - 0.05)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                <instancedMesh ref={ref_endShortSlopeRail} args={[null, null, trussModelPosInfoArr.length * 2]} frustumCulled={false}>
                    <extrudeGeometry args={[railModel(connectRailWidth, connectRailHeight), extrudeSettings(shortSlopeModelDataArr.length - 0.05)]} />
                    <MaterialTruss normalMap={normalMap} envMap={envMap} />
                </instancedMesh>
                {/* ------------------------- */}
            </>
        </group>
    )
}

// const BaseRailModel = ({ normalMap, envMap, meshDelModel_CSG }) => {
//     const obj_baseRail = new THREE.Object3D();

//     const ref_baseRail = useRef();


//     const railWidth = 0.2;
//     const railHeight = 0.2;
//     const outInDistance = useMemo(() => {
//         return (const_var.legstype === "double") ? 0.21 : 
//        (const_var.legstype === "ladder") ? 0.5 : 
//        (const_var.legstype === "zigzag") ? 1 : 0;
//     }, [const_var.legstype]);

//     const roofAlpha = Math.atan(trussDimension.roofPitch / 12);

//     const alignPosOffset = useMemo(() => {
//         if(params.p_r_s === 1 ) return 0.36 + (trussDimension.roofPitch - 3) * 0.07 / 3;
//         else if(params.p_r_s === 2 || params.p_r_s === 3) return (trussDimension.roofPitch - 3) * 0.04;
//         else return 0;
//     }, [params.p_r_s, trussDimension.roofPitch])

//     const roofBaseRailArr = useMemo(() => {
//         let dataInfoArray = [];
//         let count, distance;

//         const initRoofSpace = 0.5;
//         let initDistance = 3.5;
//         const tempCount = Math.floor(((trussDimension.buildingWidth / 2 - initRoofSpace)) / initDistance);
//         if (((trussDimension.buildingWidth / 2 - initRoofSpace)) === tempCount * initDistance) {
//             count = tempCount;
//             distance = initDistance;
//         } else if (((trussDimension.buildingWidth / 2 - initRoofSpace)) - tempCount * initDistance <= initDistance / 2) {
//             count = tempCount;
//             distance = ((trussDimension.buildingWidth / 2 - initRoofSpace)) / count;
//         } else {
//             count = tempCount + 1;
//             distance = ((trussDimension.buildingWidth / 2 - initRoofSpace)) / count;
//         }

//         for (let i = 0; i < count; i++) {
//             const pos_x = initRoofSpace + distance * i - railWidth / 2;
//             const pos_y = trussDimension.buildingHeight + ((trussDimension.buildingWidth / 2 - initRoofSpace) - pos_x) * (trussDimension.roofPitch / 12) + railHeight / 2;
            
//             const tempInfoObj = {
//                 distance: distance,
//                 pos_x: pos_x,
//                 pos_y: pos_y,
//                 length: params.p_d
//             }

//             dataInfoArray.push(tempInfoObj);
//         }

//         return dataInfoArray
//     }, [trussDimension.buildingHeight, params.p_d, trussDimension.buildingWidth, trussDimension.roofPitch])
    
//     const CSGSideBottomRailData = useMemo(() => {
//         const modelShape = railModel(railWidth, railHeight);

//         const meshBottomRailModel = new THREE.Mesh(
//             new THREE.ExtrudeGeometry(modelShape, extrudeSettings(params.p_d)),
//             new THREE.MeshStandardMaterial({ color: 'pink' })
//         )
        
//         let meshLeftBottomRailModel_Temp = meshBottomRailModel.clone();    
//         meshLeftBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2);
//         meshLeftBottomRailModel_Temp.updateMatrix();
//         meshLeftBottomRailModel_Temp.updateMatrixWorld();
//         const meshLeftBottomRailModel_CSG = CSG.fromMesh(meshLeftBottomRailModel_Temp);

//         let meshRightBottomRailModel_Temp = meshBottomRailModel.clone();    
//         meshRightBottomRailModel_Temp.position.set(0, railWidth / 2, -params.p_d / 2);
//         meshRightBottomRailModel_Temp.updateMatrix();
//         meshRightBottomRailModel_Temp.updateMatrixWorld();
//         const meshRightBottomRailModel_CSG = CSG.fromMesh(meshRightBottomRailModel_Temp);

//         const leftBottomRailModel_CSG = meshLeftBottomRailModel_CSG.subtract(meshDelModel_CSG.left);
//         const rightBottomRailModel_CSG = meshRightBottomRailModel_CSG.subtract(meshDelModel_CSG.right);

//         const CSGLeftBottomRailModel = CSG.toMesh(
//             leftBottomRailModel_CSG,
//             new THREE.Matrix4(),
//             new THREE.MeshStandardMaterial({ color: 'blue' })
//         )

//         const CSGRightBottomRailModel = CSG.toMesh(
//             rightBottomRailModel_CSG,
//             new THREE.Matrix4(),
//             new THREE.MeshStandardMaterial({ color: 'blue' })
//         )

//         return { CSGLeftBottomRailModel, CSGRightBottomRailModel };
//     }, [params.p_d, meshDelModel_CSG.left, meshDelModel_CSG.right])
    

//     useEffect(() => {
//         if (ref_baseRail.current) {
//             let index_all = 0;
            
//             new Array(2).fill('').forEach((_, index_dir) => {
//                 let offsetForDir = 1;
//                 if (index_dir === 1) offsetForDir = 1;
//                 else offsetForDir = -1;

//                 roofBaseRailArr.map((item_roof_rail, index_truss) => {
//                     //----- base rail -----
//                     obj_baseRail.position.set(offsetForDir * (item_roof_rail.pos_x), item_roof_rail.pos_y + alignPosOffset, -params.p_d / 2);
//                     obj_baseRail.rotation.z = offsetForDir * -roofAlpha;

//                     obj_baseRail.updateMatrix();
//                     ref_baseRail.current.setMatrixAt(index_all, obj_baseRail.matrix);
//                     //---------------------
//                     index_all ++;
//                 })
//             })
//             ref_baseRail.current.instanceMatrix.needsUpdate = true;
//         }
//     }, [ref_baseRail.current, roofBaseRailArr, roofAlpha, params.p_d, params.p_r_s, alignPosOffset])

//     return (
//         <group name='CTCT-baserail-legs'>
//             <>
//                 <mesh name='left-outer' geometry={CSGSideBottomRailData.CSGLeftBottomRailModel.geometry} position={[-(trussDimension.buildingWidth / 2 - railHeight / 2), 0, 0]}>
//                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                 </mesh>
                
//                 <mesh name='right-outer' geometry={CSGSideBottomRailData.CSGRightBottomRailModel.geometry} position={[(trussDimension.buildingWidth / 2 - railHeight / 2), 0, 0]}>
//                     <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                 </mesh>
                
//                 {(params.p_e_l == true && (const_var.legstype =="double" || const_var.legstype == "ladder" || const_var.legstype == 'zigzag'))?
//                 <>
//                     <mesh name='left-inner' geometry={CSGSideBottomRailData.CSGLeftBottomRailModel.geometry} position={[-(trussDimension.buildingWidth / 2 - railHeight / 2 - outInDistance), 0, 0]}>
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                     </mesh>
//                     <mesh name='right-inner' geometry={CSGSideBottomRailData.CSGRightBottomRailModel.geometry} position={[(trussDimension.buildingWidth / 2 - railHeight / 2 - outInDistance), 0, 0]}>
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                     </mesh>
//                 </>:null}
//             </>

//             {new Array(2).fill('').map((_, index_invert) =>
//                 <group name='base-rail-model-legs' key={`base-rail-model-${index_invert}`}>
//                     <mesh name='unper' position={[Math.pow(-1, index_invert) * (trussDimension.buildingWidth / 2 - railHeight / 2 - 0.05), trussDimension.buildingHeight - railHeight / 2, -params.p_d / 2]}>
//                         <extrudeGeometry args={[railModel(railWidth, railHeight), extrudeSettings(params.p_d)]} />
//                         <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4}  />
//                     </mesh>
//                 </group>
//             )}

//             {/* --- roof base rail model --- */}
//             <instancedMesh ref={ref_baseRail} args={[null, null, roofBaseRailArr.length * 2]} frustumCulled={false}>
//                 <extrudeGeometry args={[roofBaseRailModel(), extrudeSettings(params.p_d)]} />
//                 <meshPhongMaterial color={"#F0F0F0"} map={normalMap} envMap={envMap} side={THREE.DoubleSide} reflectivity={0.4} />
//             </instancedMesh>
//             {/* ------------------------- */}
//         </group>
//     )
// }